import { format, formatDistanceToNow, parseISO } from 'date-fns';

/**
 * Normalise a date value so it is always parsed as UTC.
 *
 * Spring's LocalDateTime serialises without a timezone designator
 * (e.g. "2026-04-26T16:46:07"). parseISO treats such strings as
 * *local* time, causing timestamps to appear offset by the browser's
 * UTC offset. Appending "Z" forces UTC interpretation so the browser
 * correctly converts to local time for display.
 *
 * Strings that already carry a designator ("Z", "+", "-" after the
 * time component) are returned unchanged.
 *
 * @param {string|Date} date
 * @returns {Date}
 */
const toUtcDate = (date) => {
  if (!date) return date;
  if (date instanceof Date) return date;
  // Already has timezone info — leave as-is
  if (/([Zz]|[+-]\d{2}:\d{2}|[+-]\d{4})$/.test(date)) {
    return parseISO(date);
  }
  // No timezone designator — treat as UTC
  return parseISO(date + 'Z');
};

/**
 * Format currency value
 * @param {number} value - Currency value
 * @param {string} currency - Currency code (default: LRD)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = 'LRD') => {
  if (value === null || value === undefined) return 'N/A';
  
  const formatter = new Intl.NumberFormat('en-RW', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return formatter.format(value);
};

/**
 * Format large numbers in compact form
 * @param {number} value - Number value
 * @returns {string} Formatted compact number
 */
export const formatCompactNumber = (value) => {
  if (value === null || value === undefined) return '0';
  
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  } else if (value >= 1000) {
    return (value / 1000).toFixed(1) + 'K';
  }
  
  return value.toString();
};

/**
 * Format date
 * @param {string|Date} date - Date to format
 * @param {string} formatStr - Format string (date-fns format)
 * @returns {string} Formatted date string
 */
export const formatDate = (date, formatStr = 'PPp') => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = toUtcDate(date);
    return format(dateObj, formatStr);
  } catch (error) {
    console.error('Date formatting error:', error);
    return 'Invalid date';
  }
};

/**
 * Format relative time
 * @param {string|Date} date - Date to format
 * @returns {string} Relative time string (e.g., "2 minutes ago")
 */
export const formatRelativeTime = (date) => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = toUtcDate(date);
    return formatDistanceToNow(dateObj, { addSuffix: true });
  } catch (error) {
    console.error('Relative time formatting error:', error);
    return 'Unknown';
  }
};

/**
 * Calculate percentage
 * @param {number} part - Part value
 * @param {number} total - Total value
 * @returns {string} Percentage string with % sign
 */
export const calculatePercentage = (part, total) => {
  if (!total || total === 0) return '0%';
  return ((part / total) * 100).toFixed(1) + '%';
};

/**
 * Get status badge CSS class
 * @param {string} status - Status code
 * @returns {string} CSS class name
 */
export const getStatusBadgeClass = (status) => {
  if (!status) return 'badge-info';
  
  const statusUpper = status.toUpperCase();
  
  // Success statuses
  if (['ACCP', 'ACSC', 'ACSP', 'ACTC'].includes(statusUpper)) {
    return 'badge-success';
  }
  
  // Pending statuses
  if (['PDNG', 'ACWP', 'PART'].includes(statusUpper)) {
    return 'badge-warning';
  }
  
  // Failed statuses
  if (['RJCT', 'CANC'].includes(statusUpper)) {
    return 'badge-danger';
  }
  
  return 'badge-info';
};

/**
 * Get human-readable status text
 * @param {string} status - Status code
 * @returns {string} Human-readable status
 */
export const getStatusText = (status) => {
  if (!status) return 'Unknown';
  
  const statusMap = {
    'ACCP': 'Accepted',
    'ACSC': 'Completed',
    'ACSP': 'Processing',
    'ACTC': 'Technical Acceptance',
    'PDNG': 'Pending',
    'ACWP': 'Accepted with Changes',
    'PART': 'Partially Accepted',
    'RJCT': 'Rejected',
    'CANC': 'Cancelled',
    'UNKN': 'Unknown'
  };
  
  return statusMap[status.toUpperCase()] || status;
};

/**
 * Get direction badge CSS class
 * @param {string} direction - Direction value
 * @returns {string} CSS class name
 */
export const getDirectionBadgeClass = (direction) => {
  if (!direction) return 'badge-info';
  
  const dirUpper = direction.toUpperCase();
  
  if (dirUpper === 'INBOUND' || dirUpper === 'INCOMING') {
    return 'badge bg-blue-100 text-blue-800';
  }
  
  if (dirUpper === 'OUTBOUND' || dirUpper.startsWith('OUTGOING')) {
    return 'badge bg-purple-100 text-purple-800';
  }
  
  return 'badge-info';
};

/**
 * Truncate string
 * @param {string} str - String to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated string
 */
export const truncate = (str, maxLength = 50) => {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (fallbackError) {
      console.error('Fallback copy failed:', fallbackError);
      return false;
    }
  }
};

/**
 * Download file
 * @param {string} content - File content
 * @param {string} filename - Filename
 * @param {string} mimeType - MIME type
 */
export const downloadFile = (content, filename, mimeType = 'text/plain') => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Validate JSON string
 * @param {string} jsonString - JSON string to validate
 * @returns {Object} Validation result with isValid flag and data/error
 */
export const validateJson = (jsonString) => {
  try {
    const data = JSON.parse(jsonString);
    return { isValid: true, data };
  } catch (error) {
    return { isValid: false, error: error.message };
  }
};

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Format file size
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Generate random ID
 * @param {string} prefix - Prefix for the ID
 * @returns {string} Random ID
 */
export const generateId = (prefix = 'id') => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Check if object is empty
 * @param {Object} obj - Object to check
 * @returns {boolean} True if empty
 */
export const isEmpty = (obj) => {
  return Object.keys(obj).length === 0;
};

/**
 * Deep clone object
 * @param {*} obj - Object to clone
 * @returns {*} Cloned object
 */
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Why a request did not succeed, in one line.
 *
 * The engine stores whatever closed a party lookup, quote or transfer in
 * callbackPayload: the Hub's own error body when the Hub answered with one,
 * or a sentence when the request never reached the Hub at all. Both are shown
 * to the participant the same way — an error code with its description where
 * there is one, otherwise the sentence as stored.
 *
 * The same field holds the answer to a request that succeeded, which is a
 * document rather than a reason, so a body carrying no error reads as nothing
 * to say. Callers should ask only about records that did not succeed.
 *
 * @param {string} payload - the stored callback payload, if any
 * @returns {string|null} one line, or null when there is nothing to say
 */
export const failureReason = (payload) => {
  if (!payload || typeof payload !== 'string' || !payload.trim()) return null;
  const text = payload.trim();

  // The body is sometimes quoted inside a longer message ("The Hub refused
  // the request: 404 NOT_FOUND {…}"), so the object is looked for within.
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start >= 0 && end > start) {
    const object = text.slice(start, end + 1);
    // As stored first: a body quoted inside a message may have lost its
    // escapes, but unescaping a sound body would break it.
    const attempts = [object];
    const unescaped = object.replace(/\\"/g, '"');
    if (unescaped !== object) attempts.push(unescaped);

    for (const attempt of attempts) {
      let body;
      try {
        body = JSON.parse(attempt);
      } catch {
        continue;   // Not this reading of it; try the other, else the text.
      }
      const error = body?.errorInformation || body?.errorInfo || body;
      const code = error?.errorCode;
      const description = error?.errorDescription || error?.message;
      if (code && description) return `${code} — ${description}`;
      if (description) return String(description);
      if (code) return String(code);
      // It parsed, and says nothing about an error: a successful callback's
      // own body. Printing it would fill the column with someone's party.
      if (start === 0 && end === text.length - 1) return null;
    }
  }
  return text;
};
